import { cn } from '@/lib/ui/cn';
import { cva, type VariantProps } from 'class-variance-authority';
import Link from 'next/link';
import * as React from 'react';
import { SlotButton } from './SlotButton';

const buttonVariants = cva(
  [
    'cursor-pointer',
    'inline-flex',
    'items-center',
    'justify-center',
    'gap-2',
    'whitespace-nowrap',
    'rounded-4xl',
    'text-lg',
    'md:text-2xl',
    'font-medium',
    'transition-all',
    'disabled:pointer-events-none',
    'disabled:opacity-50',
    'shrink-0',
    'outline-none',
    'focus-visible:border-sh-sky',
    'focus-visible:ring-sh-sky/50',
    'focus-visible:ring-[3px]',
    'w-full',
    'md:w-auto',
    'opacity-82',
  ],
  {
    variants: {
      variant: {
        default: ['bg-sh-sky', 'text-white', 'shadow-md', 'hover:bg-sh-brown'],
        light: [
          'bg-white/80',
          'text-sh-dark-gray',
          'shadow-md',
          'hover:bg-sh-gray',
          'hover:cursor-pointer',
          'hover:text-sh-white',
          'hover:opacity-100',
        ],
        dark: [
          'bg-sh-dark-gray/90',
          'text-sh-white',
          'shadow-md',
          'hover:bg-sh-gray',
          'hover:cursor-pointer',
          'hover:text-sh-white',
          'hover:opacity-100',
        ],
        outline: [
          'bg-transparent',
          'border',
          'border-sh-yellow',
          'text-sh-yellow',
          'shadow-md',
          'hover:bg-sh-brown',
          'hover:text-sh-white/90',
        ],
        ghost: [
          'bg-transparent',
          'text-current',
          'shadow-none',
          'hover:bg-sh-gray/30',
          'hover:opacity-100',
          'opacity-100',
        ],
      },
      size: {
        icon: 'h-9 w-9 p-2 flex items-center justify-center',
        // The small and medium are the same as the large.
        // TBD: Update small and medium sizes, or remove alternative sizes.
        small: 'h-11 py-3 px-8 text-lg md:text-xl',
        medium: 'h-11 py-3 px-8 text-lg md:text-xl',
        large: 'h-11 py-3 px-8 text-lg md:text-xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'medium',
    },
  }
);

type ButtonProps = VariantProps<typeof buttonVariants> & {
  asChild?: boolean;
  href?: string;
} & (
    | React.ButtonHTMLAttributes<HTMLButtonElement>
    | React.AnchorHTMLAttributes<HTMLAnchorElement>
  );

const Button = ({
  className,
  variant,
  size,
  asChild = false,
  href,
  ...props
}: ButtonProps) => {
  const classNames = cn(buttonVariants({ variant, size, className }));

  if (href) {
    // Only pass anchor props to Link
    const { ...anchorProps } =
      props as React.AnchorHTMLAttributes<HTMLAnchorElement>;
    return <Link href={href} className={classNames} {...anchorProps} />;
  }

  const Comp = asChild ? SlotButton : 'button';

  // Only pass button props to button
  const buttonProps = props as React.ButtonHTMLAttributes<HTMLButtonElement>;

  return <Comp data-slot='button' className={classNames} {...buttonProps} />;
};

export { Button, buttonVariants };
